'use client';

import React, { useState, useEffect } from 'react';
import { logger } from '@/utils/logger';
import { supabase } from '@/lib/supabase';
import { Database } from '@/lib/database.types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, X, ArrowUp, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';

type Ingredient = Database['public']['Tables']['ingredients']['Row'];

interface IngredientsProps {
  productId: string;
  isEditable?: boolean;
}

export function Ingredients({ productId, isEditable = false }: IngredientsProps) {
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [newIngredient, setNewIngredient] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadIngredients();
  }, [productId]);

  async function loadIngredients() {
    try {
      const { data, error } = await supabase
        .from('ingredients')
        .select('*')
        .eq('product_id', productId)
        .order('order_index');

      if (error) throw error;
      setIngredients(data || []);
    } catch (error) {
      logger.error('Error loading ingredients:', error);
      toast.error('Failed to load ingredients');
    } finally {
      setIsLoading(false);
    }
  }

  async function addIngredient(e: React.FormEvent) {
    e.preventDefault();
    if (!newIngredient.trim()) return;

    try {
      const { error } = await supabase.from('ingredients').insert({
        product_id: productId,
        name: newIngredient.trim(),
        order_index: ingredients.length,
      });

      if (error) throw error;
      setNewIngredient('');
      loadIngredients();
      toast.success('Ingredient added');
    } catch (error) {
      logger.error('Error adding ingredient:', error);
      toast.error('Failed to add ingredient');
    }
  }

  async function removeIngredient(id: string) {
    try {
      const { error } = await supabase
        .from('ingredients')
        .delete()
        .eq('id', id);

      if (error) throw error;
      loadIngredients();
      toast.success('Ingredient removed');
    } catch (error) {
      logger.error('Error removing ingredient:', error);
      toast.error('Failed to remove ingredient');
    }
  }

  async function moveIngredient(id: string, direction: 'up' | 'down') {
    const currentIndex = ingredients.findIndex((i) => i.id === id);
    if (
      (direction === 'up' && currentIndex === 0) ||
      (direction === 'down' && currentIndex === ingredients.length - 1)
    ) {
      return;
    }

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    const newIngredients = [...ingredients];
    const [movedItem] = newIngredients.splice(currentIndex, 1);
    newIngredients.splice(newIndex, 0, movedItem);

    try {
      const updates = newIngredients.map((ingredient, index) => ({
        id: ingredient.id,
        order_index: index,
      }));

      const { error } = await supabase
        .from('ingredients')
        .upsert(updates);

      if (error) throw error;
      loadIngredients();
    } catch (error) {
      logger.error('Error reordering ingredients:', error);
      toast.error('Failed to reorder ingredients');
    }
  }

  if (isLoading) {
    return <div>Loading ingredients...</div>;
  }

  return (
    <div className="space-y-4">
      <h3 className="font-semibold">Ingredienser</h3>
      
      {ingredients.length > 0 ? (
        <ul className="space-y-2">
          {ingredients.map((ingredient, index) => (
            <li
              key={ingredient.id}
              className="flex items-center justify-between gap-2 text-sm"
            >
              <span>{ingredient.name}</span>
              {isEditable && (
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => moveIngredient(ingredient.id, 'up')}
                    disabled={index === 0}
                  >
                    <ArrowUp className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => moveIngredient(ingredient.id, 'down')}
                    disabled={index === ingredients.length - 1}
                  >
                    <ArrowDown className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-destructive"
                    onClick={() => removeIngredient(ingredient.id)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      ) : isEditable && (
        <p className="text-sm text-muted-foreground">Inga ingredienser tillagda</p>
      )}

      {isEditable && (
        <form onSubmit={addIngredient} className="flex gap-2">
          <Input
            type="text"
            value={newIngredient}
            onChange={(e) => setNewIngredient(e.target.value)}
            placeholder="Lägg till ingrediens..."
            className="flex-1"
          />
          <Button type="submit" size="icon" disabled={!newIngredient.trim()}>
            <Plus className="h-4 w-4" />
          </Button>
        </form>
      )}
    </div>
  );
} 